import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import apiClient from "../../services/api";
import { formatPhoneNumber, isCompletePhone } from "../../lib/formatters";

const EMPTY = { first_name: "", last_name: "", phone: "", email: "", position: "mason", years_experience: "", language: "en", note: "" };

const AddApplicantDialog = ({ open, onOpenChange, onAdded }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = (v) => { if (!v) setForm(EMPTY); onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) return toast.error("First and last name are required");
    if (!isCompletePhone(form.phone)) return toast.error("Enter a complete 10-digit phone number");
    setSaving(true);
    try {
      const { data } = await apiClient.post("/applications/manual", {
        ...form,
        years_experience: form.position === "mason" && form.years_experience !== "" ? Number(form.years_experience) : null,
      });
      toast.success(`${data.application.first_name} ${data.application.last_name} added`);
      onAdded?.(data.application);
      close(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add applicant");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg" data-testid="add-applicant-dialog">
        <DialogHeader><DialogTitle>Add Applicant</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="aa-first">First Name *</Label>
              <Input id="aa-first" autoComplete="given-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="add-applicant-first-name" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="aa-last">Last Name *</Label>
              <Input id="aa-last" autoComplete="family-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="add-applicant-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="aa-phone">Phone *</Label>
              <Input id="aa-phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", formatPhoneNumber(e.target.value))} placeholder="(254) 555-1234" required data-testid="add-applicant-phone" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="aa-email">Email</Label>
              <Input id="aa-email" type="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="add-applicant-email" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Position</Label>
              <Select value={form.position} onValueChange={(v) => set("position", v)}>
                <SelectTrigger data-testid="add-applicant-position"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mason">Mason</SelectItem>
                  <SelectItem value="laborer">Laborer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="aa-years">Yrs exp</Label>
              <Input id="aa-years" inputMode="numeric" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value.replace(/\D/g, "").slice(0, 2))} disabled={form.position !== "mason"} placeholder={form.position === "mason" ? "e.g. 5" : "—"} data-testid="add-applicant-years" />
            </div>
            <div className="space-y-1">
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => set("language", v)}>
                <SelectTrigger data-testid="add-applicant-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="es">Spanish</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="aa-note">Note (optional)</Label>
            <Textarea id="aa-note" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} placeholder="Walk-in, referred by…, call back after 3pm…" data-testid="add-applicant-note" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => close(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="add-applicant-submit">{saving ? "Adding…" : "Add Applicant"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddApplicantDialog;
